import { finalize } from 'rxjs';

import { inject } from '@angular/core';

import { setCompletedState, setPendingState, withRequestStatus } from '@mitsubishi/utils';

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import {
  ConfigurationRequest,
  FreestDatasheetPdfRequest,
  OptionalSection,
  SheetRequest,
} from '../models/chiller-free-st.model';
import { ChillerFreeStService } from '../services/chiller-free-st.service';
import { ChillerFreeStStore } from './chiller-free-st.store';

interface ChillerFreeStDataSheetState {
  configuration: ConfigurationRequest | null;
  configurationError: boolean;
  pdfUrl: string | null;
  pdfLoading: boolean;
  pdfError: boolean;
  optionalSections: OptionalSection[] | null;
  lastSheetRequest: SheetRequest | null;
}

const initialState: ChillerFreeStDataSheetState = {
  configuration: null,
  configurationError: false,
  pdfUrl: null,
  pdfLoading: false,
  pdfError: false,
  optionalSections: null,
  lastSheetRequest: null,
};

export const ChillerFreeStDataSheetStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withMethods((store, service = inject(ChillerFreeStService), mainStore = inject(ChillerFreeStStore)) => {
    const buildPdfRequest = (sheetRequest: SheetRequest): FreestDatasheetPdfRequest => ({
      sheetRequest,
      optionalSections: store.optionalSections(),
      uomSystem: mainStore.uomSystem() || 'SI',
    });

    return {
      loadConfiguration: (configurationId: string) => {
        patchState(store, setPendingState());

        service
          .getConfiguration(configurationId)
          .pipe(finalize(() => patchState(store, setCompletedState())))
          .subscribe({
            next: config => {
              mainStore.loadStart(config.uomSystem ?? 'SI');
              patchState(store, {
                configuration: config,
              });
            },
            error: () => patchState(store, { configurationError: true }),
          });
      },
      setOptionalSections: (sections: OptionalSection[]) => {
        patchState(store, { optionalSections: sections });
      },
      getDatasheetPdfRequest: (): FreestDatasheetPdfRequest | null => {
        const sheetRequest = store.lastSheetRequest();
        return sheetRequest ? buildPdfRequest(sheetRequest) : null;
      },
      generatePdf: (sheetRequest: SheetRequest) => {
        patchState(store, { pdfLoading: true, pdfError: false, pdfUrl: null, lastSheetRequest: sheetRequest });

        service
          .generateDatasheetPdf(mainStore.projectId(), buildPdfRequest(sheetRequest))
          .pipe(finalize(() => patchState(store, { pdfLoading: false })))
          .subscribe({
            next: response => patchState(store, { pdfUrl: response.url }),
            error: () => patchState(store, { pdfError: true }),
          });
      },
      regeneratePdf: () => {
        const lastRequest = store.lastSheetRequest();
        if (!lastRequest) return;

        patchState(store, { pdfLoading: true, pdfError: false, pdfUrl: null });

        service
          .generateDatasheetPdf(mainStore.projectId(), buildPdfRequest(lastRequest))
          .pipe(finalize(() => patchState(store, { pdfLoading: false })))
          .subscribe({
            next: response => patchState(store, { pdfUrl: response.url }),
            error: () => patchState(store, { pdfError: true }),
          });
      },
    };
  })
);
